import * as React from 'react';
import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import classNames from 'classnames';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getBidOptions } from '@/redux/modules/bidOptions';
import { getCatalog } from '@/redux/modules/catalog';
import { getItem } from '@/redux/modules/item';
import { getLiveItemStatus } from '@/redux/modules/liveItemStatus';
import isEqual from 'react-fast-compare';
import { isUserLoggedIn } from '@/redux/modules/user';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import styles from './ItemBiddingStatusRow.module.scss';
import { useSelector } from 'react-redux';

type Props = {
    catalogId: number;
    itemId: number;
    toggleBidHistory: (event: React.SyntheticEvent) => void;
};

const ItemBiddingStatusRow = (props: Props) => {
    const { catalogId, itemId, toggleBidHistory } = props;

    const { currency = 'usd' } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const {
        bidActivityCount = 0,
        isLocked = false,
        isPassed = false,
        isSold = false,
        leadingBid = 0,
        salePrice = 0,
    } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { startPrice = 1 } = useSelector((state) => getItem(state, itemId), isEqual);
    const { isCatalogOnly } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const userLoggedIn = useSelector((state) => isUserLoggedIn(state), isEqual);
    const { open: isLotOpen } = useSelector((state) => getLiveItemStatus(state, itemId), isEqual);
    const bidOptions = useSelector((state) => getBidOptions(state, itemId), isEqual);

    const firstBidOption = bidOptions?.[0] || 0;
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const askPrice = leadingBid || firstBidOption;

    let lotEndPrice = null;

    if (isPassed) {
        lotEndPrice = <FormattedMessage id="lot_passed_not_disclosed" />;
    } else if (isUnsold) {
        lotEndPrice = <FormattedMessage id="lot_unsold_pending" />;
    } else if (isSold) {
        lotEndPrice = salePrice ? (
            <FormattedMessage
                id="sold_for"
                values={{
                    amount: (
                        <FormattedCurrency
                            className={styles.soldForCurrency}
                            conversionH3
                            convert
                            noFraction
                            sourceCurrency={currency}
                            value={salePrice}
                        />
                    ),
                }}
            />
        ) : (
            <FormattedCurrency conversionH3 convert noFraction sourceCurrency={currency} value={firstBidOption} />
        );
    }

    const priceText = (
        <span className={styles.price}>
            <FormattedCurrency conversionH3 convert noFraction sourceCurrency={currency} value={askPrice} />
        </span>
    );
    const countText = (
        <span className={styles.countContainer}>
            <span className={styles.count}>
                {bidActivityCount}{' '}
                {bidActivityCount === 1 ? (
                    <FormattedMessage id="itemBidding.bid" />
                ) : (
                    <FormattedMessage id="itemBidding.bids" />
                )}
            </span>
        </span>
    );

    return isCatalogOnly ? null : (
        <div className={styles.needsAParentThatDoesntDoAnything}>
            <div className={styles.status}>
                {isPassed || isSold || isUnsold ? (
                    <>
                        <ItemPriceGatewayContainer
                            className={styles['price-gateway-container']}
                            itemId={itemId}
                            priceGatewayIcon
                        >
                            <span className={styles.price}>{lotEndPrice}</span>
                        </ItemPriceGatewayContainer>
                        {bidActivityCount === 0 ? null : (
                            <span className={styles.countContainer}>
                                <ActionText
                                    className={classNames(styles.count, styles.sold)}
                                    onClick={toggleBidHistory}
                                >
                                    {bidActivityCount}{' '}
                                    {bidActivityCount === 1 ? (
                                        <FormattedMessage id="itemBidding.bid" />
                                    ) : (
                                        <FormattedMessage id="itemBidding.bids" />
                                    )}
                                </ActionText>
                            </span>
                        )}
                    </>
                ) : (
                    <>
                        {priceText}
                        {countText}
                    </>
                )}
            </div>
            {isPassed && userLoggedIn && (
                <div className={styles.passed}>
                    <FormattedMessage
                        id="starting_bid_lot_passed"
                        values={{
                            amount: <FormattedCurrency noFraction sourceCurrency={currency} value={startPrice} />,
                        }}
                    />
                </div>
            )}
            {isUnsold && userLoggedIn && (
                <div className={styles.passed}>
                    <FormattedMessage id="lot_unsold_pending" />
                </div>
            )}
        </div>
    );
};

export default React.memo<Props>(ItemBiddingStatusRow, isEqual);
